import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Button, Badge } from 'reactstrap';
import { ShoppingBag, Package, CreditCard, DollarSign, Home, MapPin, Briefcase } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../Config/api';
import authHeader from './authHeader';

const StatCard = ({ icon: Icon, title, value, color }) => (
  <Col xl="3" md="6" sm="6">
    <Card className="o-hidden">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className={`flex-shrink-0 bg-${color} rounded-circle p-3`}>
            <Icon className="text-white" size={24} />
          </div>
          <div className="flex-grow-1 ms-3">
            <h6 className="mb-1 text-muted">{title}</h6>
            <h3 className="mb-0 fw-bold">{value}</h3>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dash, ords] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/customer/dashboard`, authHeader()),
          axios.get(`${API_BASE_URL}/api/customer/orders`, authHeader()),
        ]);
        setData(dash.data);
        setOrders(ords.data.orders || []);
      } catch (e) {
        console.error('Failed to load customer dashboard', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = data?.stats || { totalOrders: 0, ordersShipped: 0, ordersPaid: 0, totalIncome: 0 };
  const accounts = data?.accounts || [];
  const stores = data?.stores || [];
  const multiStore = stores.length > 1;
  const multiAccount = accounts.length > 1;

  // Jump straight into the order form with this store already chosen, so
  // picking a store here is the first step of placing its order.
  const orderFor = (store) =>
    navigate(`${process.env.PUBLIC_URL}/customer/place-order?store=${store.id}`);

  // With several accounts on one sign-in, naming a single one in the title
  // would misdescribe totals that span all of them.
  const title = accounts.length > 1
    ? 'My Dashboard'
    : (data?.profile?.store_name || 'My Dashboard');

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title={title} />
      {accounts.length > 1 && (
        <Container fluid={true}>
          <p className="text-muted f-12">
            Showing all {accounts.length} accounts on your sign-in: {accounts.map((a) => a.store_name).join(', ')}.
          </p>
        </Container>
      )}
      <Container fluid={true}>
        {/* Store picker. Shown first because choosing which store an order is
            for is the customer's entry point, and because a store with no
            orders yet appears nowhere else on this page. */}
        {!loading && stores.length > 0 && (
          <Row>
            <Col lg="12">
              <Card>
                <CardHeader className="pb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1">{multiStore ? 'Select a store to order for' : 'My Store'}</h5>
                    <span className="text-muted f-12">
                      {multiStore
                        ? `You have ${stores.length} stores${multiAccount ? ` across ${accounts.length} accounts` : ''}. Pick the one this order ships to.`
                        : 'Your shipping destination.'}
                    </span>
                  </div>
                  <Button color="link" className="p-0"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/customer/stores`)}>
                    Manage stores
                  </Button>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    {stores.map((s) => (
                      <Col key={s.id} xl="4" md="6">
                        <div className="rf-entity-card h-100 d-flex flex-column justify-content-between">
                          <div className="d-flex align-items-start gap-2 mb-3">
                            <span className="rf-entity-avatar"><Home /></span>
                            <div className="flex-grow-1">
                              <div className="fw-semibold" style={{ fontSize: 15 }}>{s.name}</div>
                              {(s.location || s.country) && (
                                <div className="text-muted f-12 d-flex align-items-center gap-1">
                                  <MapPin size={12} /> {[s.location, s.country].filter(Boolean).join(', ')}
                                </div>
                              )}
                              {multiAccount && s.account_name && (
                                <div className="text-muted f-12 d-flex align-items-center gap-1 mt-1">
                                  <Briefcase size={12} /> {s.account_name}
                                </div>
                              )}
                            </div>
                            <Badge color={Number(s.order_count) > 0 ? 'primary' : 'light'} pill>
                              {Number(s.order_count) || 0}
                            </Badge>
                          </div>
                          <Button color="primary" outline block onClick={() => orderFor(s)}>
                            Place Order
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <StatCard icon={ShoppingBag} title="Total Orders" value={loading ? '...' : stats.totalOrders} color="primary" />
          <StatCard icon={Package} title="Orders Shipped" value={loading ? '...' : stats.ordersShipped} color="success" />
          <StatCard icon={CreditCard} title="Orders Paid" value={loading ? '...' : stats.ordersPaid} color="warning" />
          <StatCard icon={DollarSign} title="Total Paid" value={loading ? '...' : `€${Number(stats.totalIncome).toLocaleString()}`} color="info" />
        </Row>

        <Row>
          <Col lg="5">
            <Card>
              <CardHeader className="pb-0"><h5>Orders by Store</h5></CardHeader>
              <CardBody>
                {(data?.ordersByStore || []).length === 0 ? (
                  <p className="text-muted">No orders yet.</p>
                ) : (
                  <Table className="table-border-horizontal">
                    <thead><tr><th>Store</th><th>Orders</th></tr></thead>
                    <tbody>
                      {data.ordersByStore.map((s, i) => (
                        <tr key={i}><td>{s.store}</td><td>{s.orders}</td></tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="7">
            <Card>
              <CardHeader className="pb-0"><h5>Recent Orders</h5></CardHeader>
              <CardBody>
                {orders.length === 0 ? (
                  <p className="text-muted">No orders yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal">
                      <thead>
                        <tr><th>Order</th><th>Store</th><th>Date</th><th>Items</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {orders.slice(0, 8).map((o) => (
                          <tr key={o.order_id}>
                            <td><strong>{o.order_id}</strong></td>
                            <td>{o.store_name || '-'}</td>
                            <td>{formatDate(o.order_date)}</td>
                            <td>{o.item_count || 0}</td>
                            <td>{o.is_shipped ? 'Shipped' : (o.status || 'New')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CustomerDashboard;
